import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useConfirm } from '../context/ConfirmContext';
import api from '../api/axios';
import { debounce } from 'lodash';
import ReactSlider from 'react-slider';
import ScmAssigneePicker from '../components/ScmAssigneePicker';
import QuoteVariantPicker, { hasVariantOptions } from '../components/QuoteVariantPicker';
import { LOST_REASON_GROUPS, LOST_REASON_REQUIRING_NOTE, lostReasonLabel } from '../constants/lostReasons';
import './QuoteCartPage.css';

const fmt = (n) =>
    n != null
        ? new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n)
        : '—';

// ── Vertical Collapsible (for cart contents & notes) ─────────
const Collapsible = ({ label, defaultOpen = true, badge = null, children }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <div className={`qcp-collapsible ${open ? 'qcp-collapsible--open' : ''}`}>
            <button className="qcp-collapsible__trigger" onClick={() => setOpen(o => !o)}>
                <span className="qcp-collapsible__label">{label}</span>
                {badge != null && <span className="qcp-collapsible__badge">{badge}</span>}
                <svg className="qcp-collapsible__chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M6 9l6 6 6-6" />
                </svg>
            </button>
            <div className="qcp-collapsible__body">
                <div className="qcp-collapsible__inner">{children}</div>
            </div>
        </div>
    );
};

// ── Product row ───────────────────────────────────────────────
const ProductRow = ({ product, onAdd }) => {
    // Search catalog returns DTOs, so we have the inclusive price readily available
    const price = product.marketOperatingPriceWithGst || product.mrp;
    return (
        <div className="qcp-product-row">
            <div className="qcp-product-row__img">
                {product.thumbnailUrl ? <img src={product.thumbnailUrl} alt={product.name} /> : <span>📦</span>}
            </div>
            <div className="qcp-product-row__info">
                <div className="qcp-product-row__name">{product.name}</div>
                <div className="qcp-product-row__meta">
                    <span className="qcp-product-row__cat">{product.brandName || product.category}</span>
                    <span className="qcp-product-row__price">{fmt(price)}</span>
                </div>
            </div>
            <button className="qcp-product-row__add" onClick={() => onAdd(product)}>＋ Add</button>
        </div>
    );
};

// ── Cart item row ─────────────────────────────────────────────
// The eYantra discounted price is entered EXCLUDING GST. With GST added it should not exceed the
// product's MRP (which includes GST). Returns helper text to show under the box, or null when
// it's fine. A hint only — it never blocks saving or creating the quote.
const discountWarning = (item) => {
    const price = item.eYantraDiscountedPrice;
    const mrp = item.mrp || 0;
    if (price == null || mrp <= 0) return null;
    const gstRate = item.gst || 0;
    const maxExclGst = mrp / (1 + gstRate / 100);
    if (price * (1 + gstRate / 100) <= mrp + 0.005) return null;
    return `Discounted price is greater than MRP — keep it at or below ${fmt(Math.floor(maxExclGst))} excl. GST (MRP ${fmt(mrp)} incl. GST)`;
};

// Rows are keyed by item._key, not the product id — the same product can be on several lines
// (one per color/size).
const CartItemRow = ({ item, onQtyChange, onRemove, onDiscountedPriceChange, onVariantChange, isEmployee }) => {
    const warning = discountWarning(item);
    const safeQtyForMath = item.quantity === '' ? 0 : parseInt(item.quantity, 10) || 0;
    const gstRate        = item.gst || 0;
    const standardPrice  = item.marketOperatingPriceWithGst || 0;

    // Discounted price is excl. GST — add GST for the incl. total
    const activeUnitInclGst = item.eYantraDiscountedPrice != null
        ? item.eYantraDiscountedPrice * (1 + gstRate / 100)
        : standardPrice;

    return (
        <div className="qcp-cart-item">
            <div className="qcp-cart-item__img">
                {item.thumbnailUrl ? <img src={item.thumbnailUrl} alt={item.name} /> : <span>📦</span>}
            </div>
            <div className="qcp-cart-item__info">
                <div className="qcp-cart-item__name">
                    {item.name}
                    {item.selectedVariantLabel && !hasVariantOptions(item) && <span style={{ color: '#666', fontWeight: 400, marginLeft: 6 }}>— {item.selectedVariantLabel}</span>}
                </div>
                <QuoteVariantPicker item={item} value={item.selectedVariantLabel} onChange={label => onVariantChange(item._key, label)} />
                <div className="qcp-cart-item__price">
                    <span style={{ textDecoration: item.eYantraDiscountedPrice ? 'line-through' : 'none', opacity: item.eYantraDiscountedPrice ? 0.6 : 1 }}>
                        {fmt(standardPrice)}
                    </span>
                    {item.eYantraDiscountedPrice != null && (
                        <span style={{ marginLeft: '6px', color: '#ff6b35', fontWeight: 'bold' }}>
                            {fmt(activeUnitInclGst)}
                        </span>
                    )}
                    {" "}× {safeQtyForMath} = {fmt(activeUnitInclGst * safeQtyForMath)}
                    <span style={{ marginLeft: '6px', color: '#888', fontSize: '0.82em' }}>(GST {gstRate}%)</span>
                </div>
                {isEmployee && (
                    <div className="qcp-cart-item__discount-price" style={{ marginTop: '4px' }}>
                        <input
                            type="number"
                            placeholder="eYantra Discounted Price (₹)"
                            value={item.eYantraDiscountedPrice || ''}
                            onChange={(e) => {
                                const val = e.target.value ? parseFloat(e.target.value) : null;
                                onDiscountedPriceChange(item._key, val);
                            }}
                            style={{
                                width: '160px', padding: '4px 8px', fontSize: '0.85rem',
                                borderRadius: '4px', border: `1px solid ${warning ? '#e37400' : '#ccc'}`,
                                color: '#333', backgroundColor: '#fff'
                            }}
                        />
                        {warning && (
                            <div style={{ color: '#b06000', fontSize: '0.78rem', marginTop: '3px' }}>
                                ⚠ {warning}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="qcp-cart-item__actions">
                <div className="qcp-cart-item__controls">
                    <button onClick={() => onQtyChange(item._key, Math.max(1, safeQtyForMath - 1))}>−</button>
                    <input
                        type="number"
                        min="1"
                        style={{
                            width: '45px', textAlign: 'center', border: '1px solid #ccc',
                            borderRadius: '4px', height: '28px', fontSize: '0.95rem',
                            outline: 'none', appearance: 'textfield',
                            color: '#333', backgroundColor: '#fff'
                        }}
                        value={item.quantity === '' ? '' : safeQtyForMath}
                        onChange={(e) => {
                            if (e.target.value === '') {
                                onQtyChange(item._key, '');
                            } else {
                                const val = parseInt(e.target.value, 10);
                                if (!isNaN(val)) onQtyChange(item._key, val);
                            }
                        }}
                        onBlur={() => {
                            if (item.quantity === '' || item.quantity < 1) onQtyChange(item._key, 1);
                        }}
                    />
                    <button onClick={() => onQtyChange(item._key, safeQtyForMath + 1)}>+</button>
                </div>
                <button className="qcp-cart-item__remove" onClick={() => onRemove(item._key)}>✕</button>
            </div>
        </div>
    );
};

// ── Main Page ─────────────────────────────────────────────────
const SharedCartPage = () => {
    const { addToast } = useToast();
    const confirm = useConfirm();
    const [searchParams] = useSearchParams();
    const cartId = searchParams.get('id');
    const navigate = useNavigate();
    const { user } = useAuth();

    const [cart, setCart] = useState(null);
    const [items, setItems] = useState([]);
    const [clientNote,      setClientNote]      = useState('');
    const [employeeNote,    setEmployeeNote]    = useState('');
    const [clientGstin,     setClientGstin]     = useState('');
    const [billingAddress,  setBillingAddress]  = useState('');
    const [clientPhone,     setClientPhone]     = useState('');
    // Employee invoice fields
    const [modeTermsOfPayment,  setModeTermsOfPayment]  = useState('');
    const [suppliersRef,        setSuppliersRef]        = useState('');
    const [otherReferences,     setOtherReferences]     = useState('');
    const [buyerOrderNo,        setBuyerOrderNo]        = useState('');
    const [buyerOrderDated,     setBuyerOrderDated]     = useState('');
    const [despatchDocumentNo,  setDespatchDocumentNo]  = useState('');
    const [destination,         setDestination]         = useState('');
    const [despatchedType,      setDespatchedType]      = useState('');
    const [paymentTerms,        setPaymentTerms]        = useState('');
    const [termsOfDelivery,     setTermsOfDelivery]     = useState('');
    const [preparedBy,          setPreparedBy]          = useState('');
    const [deliveryCharge,      setDeliveryCharge]      = useState('');
    const [customizationCharge, setCustomizationCharge] = useState('');
    const [assignedScmEmail,    setAssignedScmEmail]    = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [successMsg, setSuccessMsg] = useState('');

    const [filtersOpen, setFiltersOpen] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [priceBounds, setPriceBounds] = useState([0, 100000]);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedBrand, setSelectedBrand] = useState('');
    const [priceRange, setPriceRange] = useState([0, 100000]);

    const [notesDefaultOpen, setNotesDefaultOpen] = useState(false);

    const isEmployee = user && ['ADMIN', 'CURATOR', 'EMPLOYEE'].includes(user.role);

    const observer = useRef();
    const lastProductElementRef = useCallback(node => {
        if (isSearching) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) setCurrentPage(p => p + 1);
        });
        if (node) observer.current.observe(node);
    }, [isSearching, hasMore]);

    useEffect(() => {
        if (!cartId) { setError("No cart ID provided."); setIsLoading(false); return; }
        api.get(`/quotes/${cartId}`)
            .then(res => {
                const data = res.data;

                if (data.status === 'QUOTE' && !isEmployee) {
                    navigate(`/quote?name=${encodeURIComponent(data.cartName)}`, { replace: true });
                    return;
                }

                setCart(data);

                setItems(data.items.map((i, idx) => {
                    const p = i.product;
                    const gstRate = p.gst || 0;
                    const basePrice = p.marketOperatingPrice || p.mrp || 0;
                    const inclusivePrice = basePrice * (1 + (gstRate / 100));

                    return {
                        _key: `${p.id}-${idx}`,
                        id: p.id,
                        name: p.name,
                        thumbnailUrl: p.thumbnailUrl,
                        variants: p.variants || [],
                        colors: p.colors || null,
                        sizes: p.sizes || null,
                        selectedVariantLabel: i.selectedVariantLabel || null,
                        gst: gstRate,
                        mrp: p.mrp,
                        marketOperatingPrice: basePrice,
                        marketOperatingPriceWithGst: inclusivePrice,
                        eYantraDiscountedPrice: i.eYantraDiscountedPrice || null,
                        quantity: i.quantity
                    };
                }));
                const cn = data.clientNote || '';
                const en = data.employeeNote || '';
                setClientNote(cn);
                setEmployeeNote(en);
                setClientGstin(data.clientGstin || '');
                setBillingAddress(data.billingAddress || '');
                setClientPhone(data.clientPhone || '');
                setModeTermsOfPayment(data.modeTermsOfPayment || '');
                setSuppliersRef(data.suppliersRef || '');
                setOtherReferences(data.otherReferences || '');
                setBuyerOrderNo(data.buyerOrderNo || '');
                setBuyerOrderDated(data.buyerOrderDated || '');
                setDespatchDocumentNo(data.despatchDocumentNo || '');
                setDestination(data.destination || '');
                setDespatchedType(data.despatchedType || '');
                setPaymentTerms(data.paymentTerms || '');
                setTermsOfDelivery(data.termsOfDelivery || '');
                setPreparedBy(data.preparedBy || '');
                setDeliveryCharge(data.deliveryCharge ?? '');
                setCustomizationCharge(data.customizationCharge ?? '');
                setAssignedScmEmail(data.assignedScmEmail || '');
                setNotesDefaultOpen(!!(cn || en));
                setIsLoading(false);
            })
            .catch(() => { setError("Failed to load cart or you do not have permission."); setIsLoading(false); });
    }, [cartId, navigate, isEmployee]);

    useEffect(() => {
        Promise.all([api.get('/categories'), api.get('/brands'), api.get('/products/price-range')])
            .then(([catRes, brandRes, rangeRes]) => {
                setCategories(catRes.data || []);
                setBrands(brandRes.data || []);
                if (rangeRes.data) {
                    const bounds = [Math.floor(rangeRes.data.minPrice), Math.ceil(rangeRes.data.maxPrice)];
                    setPriceBounds(bounds);
                    setPriceRange(bounds);
                }
            })
            .catch(err => console.error("Error fetching filters", err));
    }, []);

    const doSearch = useCallback(
        debounce(async (params, page, isNew) => {
            setIsSearching(true);
            try {
                const res = await api.get('/products', { params: { ...params, page, size: 15 } });
                const fetched = res.data?.content || [];
                setSearchResults(prev => isNew ? fetched : [...prev, ...fetched]);
                setHasMore(!res.data?.last);
            } catch { if (isNew) setSearchResults([]); }
            finally { setIsSearching(false); }
        }, 400), []
    );

    useEffect(() => { setCurrentPage(0); }, [searchTerm, selectedCategory, selectedBrand, priceRange]);
    useEffect(() => {
        doSearch({ search: searchTerm, category: selectedCategory, brand: selectedBrand, minPrice: priceRange[0], maxPrice: priceRange[1] }, currentPage, currentPage === 0);
    }, [searchTerm, selectedCategory, selectedBrand, priceRange, currentPage, doSearch]);

    // Adding a product again bumps the line that has no color/size picked yet; once every line of
    // it has one, a new line is added so the same product can be quoted in another color/size.
    const addProduct = (product) => setItems(prev => {
        const open = prev.find(i => i.id === product.id && !i.selectedVariantLabel);
        if (open) return prev.map(i => i === open ? { ...i, quantity: (parseInt(i.quantity, 10) || 0) + 1 } : i);
        const taken = new Set(prev.map(i => i._key));
        let key = String(product.id);
        for (let n = 2; taken.has(key); n++) key = `${product.id}-${n}`;

        const gstRate = product.gst || 0;
        const basePrice = product.marketOperatingPrice || product.mrp || 0;
        const inclusivePrice = product.marketOperatingPriceWithGst || (basePrice * (1 + (gstRate / 100)));

        return [...prev, {
            _key: key,
            id: product.id,
            name: product.name,
            thumbnailUrl: product.thumbnailUrl,
            variants: product.variants || [],
            colors: product.colors || null,
            sizes: product.sizes || null,
            selectedVariantLabel: null,
            gst: gstRate,
            mrp: product.mrp,
            marketOperatingPrice: basePrice,
            marketOperatingPriceWithGst: inclusivePrice,
            eYantraDiscountedPrice: null,
            quantity: 1
        }];
    });

    const updateQty = (key, qty) => setItems(prev => prev.map(i => i._key === key ? { ...i, quantity: qty } : i));
    const removeItem = (key) => setItems(prev => prev.filter(i => i._key !== key));
    const updateDiscountedPrice = (key, price) => setItems(prev => prev.map(i => i._key === key ? { ...i, eYantraDiscountedPrice: price } : i));
    const updateVariant = (key, label) => setItems(prev => prev.map(i => i._key === key ? { ...i, selectedVariantLabel: label } : i));

    const { totalBase, totalGst, totalAmount } = useMemo(() => {
        return items.reduce((acc, item) => {
            const qty     = parseInt(item.quantity, 10) || 0;
            const gstRate = item.gst || 0;

            // Discounted price is excl. GST — add GST on top
            // Standard MOP is incl. GST — derive base by dividing
            const unitBase      = item.eYantraDiscountedPrice != null
                ? item.eYantraDiscountedPrice
                : (item.marketOperatingPriceWithGst || 0) / (1 + (gstRate / 100));
            const unitInclGst   = item.eYantraDiscountedPrice != null
                ? item.eYantraDiscountedPrice * (1 + gstRate / 100)
                : (item.marketOperatingPriceWithGst || 0);

            const itemTotalAmount    = unitInclGst * qty;
            const itemBasePriceTotal = unitBase    * qty;
            const itemGstAmount      = itemTotalAmount - itemBasePriceTotal;

            return {
                totalBase:   acc.totalBase   + itemBasePriceTotal,
                totalGst:    acc.totalGst    + itemGstAmount,
                totalAmount: acc.totalAmount + itemTotalAmount
            };
        }, { totalBase: 0, totalGst: 0, totalAmount: 0 });
    }, [items]);

    const totalAmountWithCharges = totalAmount
        + (parseFloat(deliveryCharge) || 0)
        + (parseFloat(customizationCharge) || 0);

    const handleSaveChanges = async () => {
        setIsSaving(true); setError(''); setSuccessMsg('');
        try {
            await api.put(`/quotes/${cartId}/update`, buildPayload());
            setSuccessMsg("Changes saved successfully.");
            setTimeout(() => setSuccessMsg(''), 3000);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to save changes.");
        } finally { setIsSaving(false); }
    };

    const handleMoveToQuote = async () => {
        const ok = await confirm({
            title: 'Turn this cart into a quote?',
            message: 'The cart will be locked and can no longer be edited.',
            confirmLabel: 'Create quote',
        });
        if (!ok) return;
        setIsSaving(true);
        try {
            await api.put(`/quotes/${cartId}/update`, buildPayload());
            await api.post(`/quotes/${cartId}/move-to-quote`);
            navigate(`/quote?name=${encodeURIComponent(cart.cartName)}`);
        } catch (err) {
            setError(err.response?.data?.error || "Failed to move to quote.");
            setIsSaving(false);
        }
    };

    const buildPayload = () => ({
        items: items.map(i => ({
            productId: i.id,
            quantity: i.quantity,
            variantLabel: i.selectedVariantLabel || null,
            ...(i.eYantraDiscountedPrice != null && { eYantraDiscountedPrice: i.eYantraDiscountedPrice })
        })),
        clientNote,
        employeeNote,
        clientGstin,
        billingAddress,
        clientPhone,
        modeTermsOfPayment,
        suppliersRef,
        otherReferences,
        buyerOrderNo,
        buyerOrderDated,
        despatchDocumentNo,
        destination,
        despatchedType,
        paymentTerms,
        termsOfDelivery,
        preparedBy,
        deliveryCharge: deliveryCharge === '' ? null : parseFloat(deliveryCharge),
        customizationCharge: customizationCharge === '' ? null : parseFloat(customizationCharge),
        assignedScmEmail: assignedScmEmail.trim() || null,
    });

    // --- NEW FUNCTIONS ---
    const handleMarkConverted = async () => {
        const ok = await confirm({
            title: 'Mark this quote as won?',
            message: 'It will be recorded as converted to an order.',
            confirmLabel: 'Mark as won',
        });
        if (!ok) return;
        try {
            await api.put(`/quotes/${cartId}/update`, buildPayload());
            await api.post(`/quotes/${cartId}/mark-converted`);
            setCart(prev => ({ ...prev, status: 'CONVERTED' }));
            addToast('Quote marked as converted.', 'success');
        } catch (error) {
            console.error("Failed to mark as converted", error);
            addToast('Failed to update quote status.', 'error');
        }
    };

    const handleMarkLost = async () => {
        const outcome = await confirm({
            title: 'Mark this quote as lost?',
            message: 'The reason is recorded against this cart and feeds the lost-reason breakdown.',
            confirmLabel: 'Mark as lost',
            tone: 'danger',
            reasons: LOST_REASON_GROUPS,
            reasonLabel: 'Why was it lost?',
            noteRequiredFor: LOST_REASON_REQUIRING_NOTE,
        });
        if (!outcome) return;
        try {
            await api.put(`/quotes/${cartId}/update`, buildPayload());
            await api.post(`/quotes/${cartId}/mark-lost`, {
                lostReason: outcome.reason,
                lostReasonNote: outcome.note,
            });
            setCart(prev => ({
                ...prev,
                status: 'CLOSED_LOST',
                lostReason: outcome.reason,
                lostReasonNote: outcome.note,
            }));
            addToast('Quote marked as lost.', 'success');
        } catch (error) {
            console.error("Failed to mark as lost", error);
            // The backend rejects an unknown or missing reason with a 400 and an
            // explanation; surface it rather than the generic failure message.
            addToast(error?.response?.data?.error || 'Failed to update quote status.', 'error');
        }
    };

    const handleMarkHold = async () => {
        const ok = await confirm({
            title: 'Put this cart on hold?',
            message: "Follow-up reminders pause until it's resumed.",
            confirmLabel: 'Put on hold',
        });
        if (!ok) return;
        try {
            await api.put(`/quotes/${cartId}/update`, buildPayload());
            await api.post(`/quotes/${cartId}/mark-hold`);
            setCart(prev => ({ ...prev, status: 'HOLD' }));
            addToast('Cart placed on hold.', 'success');
        } catch (error) {
            console.error("Failed to mark as hold", error);
            addToast('Failed to update quote status.', 'error');
        }
    };

    const handleResumeFromHold = async () => {
        try {
            await api.post(`/quotes/${cartId}/resume-from-hold`);
            setCart(prev => ({ ...prev, status: 'CART' }));
            addToast('Cart resumed.', 'success');
        } catch (error) {
            console.error("Failed to resume from hold", error);
            addToast('Failed to update quote status.', 'error');
        }
    };
    // ----------------------

    const activeFilterCount = [
        selectedCategory !== '',
        selectedBrand !== '',
        priceRange[0] !== priceBounds[0] || priceRange[1] !== priceBounds[1]
    ].filter(Boolean).length;

    const clearFilters = () => {
        setSelectedCategory(''); setSelectedBrand(''); setPriceRange(priceBounds); setSearchTerm('');
    };

    if (isLoading) return <div className="loading-state">Loading workspace...</div>;
    if (error && !cart) return <div className="error-state">{error}</div>;

    return (
        <div className="qcp-wrapper">
            <div className="qcp-header">
                <div className="qcp-header__left">
                    <div className="qcp-header__eyebrow">Collaborative Workspace</div>
                    <h1 className="qcp-header__title">{cart.cartName}</h1>
                </div>
                <div className="qcp-header__steps">
                    {cart.status === 'QUOTE' ? (
                        <button
                            className="qcp-btn"
                            style={{ backgroundColor: '#137333', color: 'white', border: 'none' }}
                                onClick={() => navigate(`/quote?name=${encodeURIComponent(cart.cartName)}`)}
                             >
                            📄 View / Print PDF
                        </button>
                    ) : (
                        <button className="qcp-btn qcp-btn--primary" onClick={handleMoveToQuote} disabled={isSaving}>
                            🔒 Lock & Move to Quote
                        </button>
                    )}
                </div>
            </div>

            <div className={`qcp-workspace ${filtersOpen ? 'qcp-workspace--filters-open' : ''}`}>

                <aside className={`qcp-filter-drawer ${filtersOpen ? 'qcp-filter-drawer--open' : ''}`}>
                    <div className="qcp-filter-drawer__inner">
                        <div className="qcp-filter-drawer__header">
                            <span className="qcp-filter-drawer__title">Filters</span>
                            {activeFilterCount > 0 && (
                                <button className="qcp-filter-clear-inline" onClick={clearFilters}>
                                    Clear {activeFilterCount}
                                </button>
                            )}
                        </div>

                        <div className="qcp-filter-section">
                            <label className="qcp-filter-label">Category</label>
                            <select className="qcp-filter-select" value={selectedCategory} onChange={e => setSelectedCategory(e.target.value)}>
                                <option value="">All Categories</option>
                                {categories.map(cat => <option key={cat.id} value={cat.name}>{cat.name}</option>)}
                            </select>
                        </div>

                        <div className="qcp-filter-section">
                            <label className="qcp-filter-label">Brand</label>
                            <select className="qcp-filter-select" value={selectedBrand} onChange={e => setSelectedBrand(e.target.value)}>
                                <option value="">All Brands</option>
                                {brands.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
                            </select>
                        </div>

                        <div className="qcp-filter-section">
                            <label className="qcp-filter-label">Price Range</label>
                            <div className="qcp-price-info">
                                <span>{fmt(priceRange[0])}</span>
                                <span>{fmt(priceRange[1])}</span>
                            </div>
                            <ReactSlider
                                className="qcp-price-slider" thumbClassName="qcp-thumb" trackClassName="qcp-track"
                                value={priceRange} min={priceBounds[0]} max={priceBounds[1]}
                                onChange={setPriceRange} pearling minDistance={100}
                            />
                        </div>
                    </div>
                </aside>

                <div className="qcp-panel qcp-panel--search qcp-workspace__main">
                    <div className="qcp-search-toolbar">
                        <button
                            className={`qcp-filter-toggle ${filtersOpen ? 'qcp-filter-toggle--active' : ''}`}
                            onClick={() => setFiltersOpen(o => !o)}
                            title={filtersOpen ? 'Hide filters' : 'Show filters'}
                        >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="4" y1="6" x2="20" y2="6"/>
                                <line x1="8" y1="12" x2="20" y2="12"/>
                                <line x1="12" y1="18" x2="20" y2="18"/>
                            </svg>
                            Filters
                            {activeFilterCount > 0 && (
                                <span className="qcp-filter-toggle__count">{activeFilterCount}</span>
                            )}
                        </button>

                        <div className="qcp-search-wrap" style={{ flex: 1, marginBottom: 0 }}>
                            <svg className="qcp-search-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                            </svg>
                            <input
                                type="text" placeholder="Search products..."
                                value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                                className="qcp-search-input"
                            />
                            {isSearching && currentPage === 0 && <div className="qcp-search-spinner" />}
                        </div>
                    </div>

                    {activeFilterCount > 0 && (
                        <div className="qcp-active-filters">
                            {selectedCategory && (
                                <span className="qcp-filter-pill">
                                    {selectedCategory}
                                    <button onClick={() => setSelectedCategory('')}>✕</button>
                                </span>
                            )}
                            {selectedBrand && (
                                <span className="qcp-filter-pill">
                                    {selectedBrand}
                                    <button onClick={() => setSelectedBrand('')}>✕</button>
                                </span>
                            )}
                            {(priceRange[0] !== priceBounds[0] || priceRange[1] !== priceBounds[1]) && (
                                <span className="qcp-filter-pill">
                                    {fmt(priceRange[0])} – {fmt(priceRange[1])}
                                    <button onClick={() => setPriceRange(priceBounds)}>✕</button>
                                </span>
                            )}
                        </div>
                    )}

                    <div className="qcp-search-results" style={{ marginTop: '0.75rem' }}>
                        {searchResults.length > 0
                            ? searchResults.map((p, i) => i === searchResults.length - 1
                                ? <div ref={lastProductElementRef} key={p.id}><ProductRow product={p} onAdd={addProduct} /></div>
                                : <ProductRow key={p.id} product={p} onAdd={addProduct} />
                            )
                            : (searchTerm || selectedCategory || selectedBrand) && !isSearching
                                ? <div className="qcp-empty-search">No products match your criteria.</div>
                                : <div className="qcp-search-hint">
                                    <div className="qcp-search-hint__icon">🔍</div>
                                    <div>Search to find products</div>
                                  </div>
                        }
                        {isSearching && currentPage > 0 && <div className="qcp-search-hint">Loading more...</div>}
                    </div>
                </div>

                <div className="qcp-panel qcp-panel--cart qcp-workspace__cart">

                    <Collapsible
                        label="Cart Contents"
                        defaultOpen={true}
                        badge={items.length > 0 ? items.length : null}
                    >
                        {items.length === 0 ? (
                            <div className="qcp-cart-empty">
                                <div className="qcp-cart-empty__icon">🛒</div>
                                <div>No items yet.</div>
                            </div>
                        ) : (
                            <div className="qcp-cart-list">
                                {items.map(item => (
                                    <CartItemRow
                                        key={item._key}
                                        item={item}
                                        onQtyChange={updateQty}
                                        onRemove={removeItem}
                                        onDiscountedPriceChange={updateDiscountedPrice}
                                        onVariantChange={updateVariant}
                                        isEmployee={isEmployee}
                                    />
                                ))}
                            </div>
                        )}
                        <div className="qcp-cart-breakdown" style={{ marginTop: '2rem', borderTop: '1px solid #e0e0e0', paddingTop: '1.2rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#5f6368' }}>
                                <span>Subtotal (Excl. GST)</span>
                                <span>{fmt(totalBase)}</span>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: '#5f6368' }}>
                                <span>Total GST</span>
                                <span>{fmt(totalGst)}</span>
                            </div>

                            {parseFloat(deliveryCharge) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', color: '#5f6368' }}>
                                    <span>Delivery Charges</span>
                                    <span>{fmt(parseFloat(deliveryCharge))}</span>
                                </div>
                            )}
                            {parseFloat(customizationCharge) > 0 && (
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px', color: '#5f6368' }}>
                                    <span>Customization Charges</span>
                                    <span>{fmt(parseFloat(customizationCharge))}</span>
                                </div>
                            )}

                            <div className="qcp-cart-total" style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '1.2rem', borderTop: '1px dashed #e0e0e0', paddingTop: '12px' }}>
                                <span>Total Amount </span>
                                <span className="qcp-cart-total__val">{fmt(totalAmountWithCharges)}</span>
                            </div>

                            {/* --- FINALIZATION BUTTONS --- */}
                            {isEmployee && (cart.status === 'CART' || cart.status === 'REQUESTED' || cart.status === 'QUOTE') && (
                                <div style={{
                                    display: 'flex',
                                    gap: '1rem',
                                    marginTop: '2rem',
                                    justifyContent: 'flex-end',
                                    borderTop: '1px solid var(--border-color)',
                                    paddingTop: '1.5rem'
                                }}>
                                    <button
                                        onClick={handleMarkHold}
                                        style={{
                                            padding: '0.8rem 1.5rem',
                                            background: 'rgba(251, 191, 36, 0.12)',
                                            color: '#92610a',
                                            border: '1px solid rgba(251, 191, 36, 0.4)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(251, 191, 36, 0.22)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(251, 191, 36, 0.12)'}
                                    >
                                        ⏸ Put on Hold
                                    </button>

                                    <button
                                        onClick={handleMarkLost}
                                        style={{
                                            padding: '0.8rem 1.5rem',
                                            background: 'rgba(244, 67, 54, 0.1)',
                                            color: '#F44336',
                                            border: '1px solid rgba(244, 67, 54, 0.3)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            fontWeight: 'bold',
                                            transition: 'all 0.2s'
                                        }}
                                        onMouseOver={(e) => e.currentTarget.style.background = 'rgba(244, 67, 54, 0.2)'}
                                        onMouseOut={(e) => e.currentTarget.style.background = 'rgba(244, 67, 54, 0.1)'}
                                    >
                                        ❌ Mark as Lost
                                    </button>

                                    {cart.status === 'QUOTE' && (
                                        <button
                                            onClick={handleMarkConverted}
                                            style={{
                                                padding: '0.8rem 1.5rem',
                                                background: 'rgba(76, 175, 80, 0.1)',
                                                color: '#4CAF50',
                                                border: '1px solid rgba(76, 175, 80, 0.3)',
                                                borderRadius: '8px',
                                                cursor: 'pointer',
                                                fontWeight: 'bold',
                                                transition: 'all 0.2s'
                                            }}
                                            onMouseOver={(e) => e.currentTarget.style.background = 'rgba(76, 175, 80, 0.2)'}
                                            onMouseOut={(e) => e.currentTarget.style.background = 'rgba(76, 175, 80, 0.1)'}
                                        >
                                            ✅ Mark as Won (Converted)
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* --- ON HOLD BANNER + RESUME --- */}
                            {cart.status === 'HOLD' && (
                                <div style={{
                                    marginTop: '2rem',
                                    padding: '1rem',
                                    textAlign: 'center',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    background: 'rgba(251, 191, 36, 0.12)',
                                    color: '#92610a'
                                }}>
                                    This cart is currently on Hold.
                                    {isEmployee && (
                                        <div style={{ marginTop: '0.75rem' }}>
                                            <button
                                                onClick={handleResumeFromHold}
                                                style={{
                                                    padding: '0.6rem 1.25rem',
                                                    background: '#92610a',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    cursor: 'pointer',
                                                    fontWeight: 'bold'
                                                }}
                                            >
                                                ▶ Resume Cart
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* --- COMPLETED STATUS BANNER --- */}
                            {(cart.status === 'CONVERTED' || cart.status === 'CLOSED_LOST') && (
                                <div style={{
                                    marginTop: '2rem',
                                    padding: '1rem',
                                    textAlign: 'center',
                                    borderRadius: '8px',
                                    fontWeight: 'bold',
                                    background: cart.status === 'CONVERTED' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                    color: cart.status === 'CONVERTED' ? '#4CAF50' : '#F44336'
                                }}>
                                    This quote has been {cart.status === 'CONVERTED' ? 'Successfully Converted to an Order 🎉' : 'marked as Lost.'}
                                    {cart.status === 'CLOSED_LOST' && cart.lostReason && (
                                        <div style={{ marginTop: '0.4rem', fontWeight: 500, fontSize: '0.85rem' }}>
                                            {lostReasonLabel(cart.lostReason)}
                                            {cart.lostReasonNote && ` — ${cart.lostReasonNote}`}
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Collapsible>

                    <Collapsible
                        label="Quote Details"
                        defaultOpen={true}
                        badge={(clientNote || employeeNote || clientGstin || billingAddress || paymentTerms || deliveryCharge || customizationCharge || assignedScmEmail) ? '✦' : null}
                    >
                        <div className="qcp-field">
                            <label className="qcp-field__label">Assigned SCM</label>
                            <ScmAssigneePicker value={assignedScmEmail} onChange={setAssignedScmEmail} disabled={!isEmployee} />
                        </div>
                        <div className="qcp-field">
                            <label className="qcp-field__label">Client GSTIN</label>
                            <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                placeholder="e.g. 22AAAAA0000A1Z5" maxLength={15}
                                value={clientGstin} onChange={e => setClientGstin(e.target.value.toUpperCase())}
                                disabled={!isEmployee} />
                        </div>
                        <div className="qcp-field">
                            <label className="qcp-field__label">Billing Address</label>
                            <textarea className={`qcp-field__input qcp-textarea${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                rows="2" placeholder="Street, City, State, PIN code"
                                value={billingAddress} onChange={e => setBillingAddress(e.target.value)}
                                disabled={!isEmployee} />
                        </div>
                        <div className="qcp-field-row">
                            <div className="qcp-field">
                                <label className="qcp-field__label">Phone Number</label>
                                <input type="tel" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="+91 98765 43210"
                                    value={clientPhone} onChange={e => setClientPhone(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                            <div className="qcp-field">
                                <label className="qcp-field__label">Prepared By</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="Employee name"
                                    value={preparedBy} onChange={e => setPreparedBy(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                        </div>
                        <div className="qcp-field-row">
                            <div className="qcp-field">
                                <label className="qcp-field__label">Mode / Terms of Payment</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="e.g. 100% Advance"
                                    value={modeTermsOfPayment} onChange={e => setModeTermsOfPayment(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                            <div className="qcp-field">
                                <label className="qcp-field__label">Payment Terms</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="e.g. Net 30, Advance"
                                    value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                        </div>
                        <div className="qcp-field-row">
                            <div className="qcp-field">
                                <label className="qcp-field__label">Supplier's Ref.</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="Internal reference no."
                                    value={suppliersRef} onChange={e => setSuppliersRef(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                            <div className="qcp-field">
                                <label className="qcp-field__label">Other Reference(s)</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="Any other ref."
                                    value={otherReferences} onChange={e => setOtherReferences(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                        </div>
                        <div className="qcp-field-row">
                            <div className="qcp-field">
                                <label className="qcp-field__label">Buyer's Order No.</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="PO number"
                                    value={buyerOrderNo} onChange={e => setBuyerOrderNo(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                            <div className="qcp-field">
                                <label className="qcp-field__label">Buyer Order Date</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="dd/mm/yyyy"
                                    value={buyerOrderDated} onChange={e => setBuyerOrderDated(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                        </div>
                        <div className="qcp-field-row">
                            <div className="qcp-field">
                                <label className="qcp-field__label">Despatch Document No.</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="Dispatch ref. no."
                                    value={despatchDocumentNo} onChange={e => setDespatchDocumentNo(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                            <div className="qcp-field">
                                <label className="qcp-field__label">Destination</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="City / State"
                                    value={destination} onChange={e => setDestination(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                        </div>
                        <div className="qcp-field-row">
                            <div className="qcp-field">
                                <label className="qcp-field__label">Despatched through</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="e.g. By Road / Air / Surface"
                                    value={despatchedType} onChange={e => setDespatchedType(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                            <div className="qcp-field">
                                <label className="qcp-field__label">Terms of Delivery</label>
                                <input type="text" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="e.g. Ex-Works, FOB"
                                    value={termsOfDelivery} onChange={e => setTermsOfDelivery(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                        </div>
                        <div className="qcp-field-row">
                            <div className="qcp-field">
                                <label className="qcp-field__label">Delivery Charges</label>
                                <input type="number" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="0.00" min="0" step="0.01"
                                    value={deliveryCharge} onChange={e => setDeliveryCharge(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                            <div className="qcp-field">
                                <label className="qcp-field__label">Customization Charges</label>
                                <input type="number" className={`qcp-field__input${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                    placeholder="0.00" min="0" step="0.01"
                                    value={customizationCharge} onChange={e => setCustomizationCharge(e.target.value)}
                                    disabled={!isEmployee} />
                            </div>
                        </div>
                        <div className="qcp-field">
                            <label className="qcp-field__label">Client Request / Notes</label>
                            <textarea className={`qcp-field__input qcp-textarea${isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                rows="2" placeholder="Requests from the client..."
                                value={clientNote} onChange={e => setClientNote(e.target.value)}
                                disabled={isEmployee} />
                        </div>
                        <div className="qcp-field" style={{ marginBottom: 0 }}>
                            <label className="qcp-field__label">eYantra Representative Notes</label>
                            <textarea className={`qcp-field__input qcp-textarea${!isEmployee ? ' qcp-field__input--disabled' : ''}`}
                                rows="2" placeholder="Internal eYantra notes..."
                                value={employeeNote} onChange={e => setEmployeeNote(e.target.value)}
                                disabled={!isEmployee} />
                        </div>
                    </Collapsible>

                    {successMsg && <div className="qcp-success-msg">✓ {successMsg}</div>}
                    {error && <div className="qcp-error">{error}</div>}

                    <button className="qcp-btn qcp-btn--save" onClick={handleSaveChanges} disabled={isSaving}>
                        {isSaving ? 'Saving...' : '💾 Save Changes'}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default SharedCartPage;